'use strict';

const Enum = require('./enum');

/**
 * Defines identifiers for partners
 *
 * @enum
 * @readonly
 */
exports.Partners = Object.create(Enum, {
  AIRBNB: {
    value: 'airbnb',
    enumerable: true,
  },
  AIRBNB_INTERNAL: {
    value: 'Airbnb (Internal)',
    enumerable: true,
  },
  NEST: {
    value: 'nest',
    enumerable: true,
  },
  COMCAST: {
    value: 'comcast',
    enumerable: true,
  },
  HMAC_KEY: {
    value: 'ba1440fe-0690-4688-ab3e-e32cee121cbf',
    enumerable: true,
  },
});

/**
 * Defines events that apply to
 * partner-related operations.
 *
 * @enum
 * @readonly
 */
exports.PartnerEvents = Object.create(Enum, {
  CONNECT: {
    value: 'connect',
    enumerable: true,
  },
  DISCONNECT: {
    value: 'disconnect',
    enumerable: true,
  },
});

/**
 * Defines partner types.  Every partner has one type.
 *
 * @enum
 * @readonly
 */
exports.PartnerTypes = Object.create(Enum, {
  WORKS_WITH: {
    value: 'works with',
    enumerable: true,
  },
  AUGUST_ACCESS: {
    value: 'august access',
    enumerable: true,
  },
});

/**
 * This is the order we want to display the partner tags in the client apps.
 */
exports.PartnerTags = ['featured', 'smart home', 'delivery', 'home services', 'rentals', 'pet care'];

exports.PARTNER_PLATFORM_IOS = 'ios';
exports.PARTNER_PLATFORM_ANDROID = 'android';
exports.PARTNER_ID_NEST = '579a5dfaf1a1227b4049a2b1';
exports.PARTNER_ID_AIRBNB = '579a5dfaf1a1227b4049a2af'; // The partnerID field of the 'Airbnb (Internal)' oauthclient2 doc and the oauthclient.outbound doc. This is used by our system to communicate with Airbnb.
exports.PARTNER_ID_AIRBNB_EXTERNAL = '62e17c30b9094c00145d65e7'; // The partnerID field of the 'Airbnb' oauthclient2 doc used by Airbnb to communicate with our system.
exports.PARTNER_ID_KEYBYAMAZON = '5f31d6dd3e72f5000f7fcad2';
exports.PARTNER_ID_KEYBYAMAZON_YALE = '5fa99da86a89c71d5e816688';
exports.PARTNER_ID_AMAZON_ALEXA = '579a92a5e33fc1c6719a4951';
exports.PARTNER_ID_HUE = '5f19ca2a05fc94001037a79d'; // Mongo object ID from db.partners for Hue partner
exports.PARTNER_ID_HUE_YALE = 'HUE_YALE_PARTNER_ID'; // There is one partner Hue, but 2 ways to oauth, yale and august.
exports.PARTNER_ID_HOMINS = '60a58bdabfae1db6074ce5c6'; // The partnerID field of the 'homins' oauthclient2 doc.

exports.AMAZON_ALEXA_SKILL_IDS_BY_BRAND = Object.create(Enum, {
  august: {
    value: 'amzn1.ask.skill.5603d1db-4da0-4bad-90e6-1a896e58aabb',
    enumerable: true,
  },
  yale: {
    value: 'amzn1.ask.skill.85616d9b-1d42-4d16-ac61-ee7eadd4e8c3',
    enumerable: true,
  },
});

exports.AirbnbListingAccessTypes = {
  APP_AND_KEYPAD_ACCESS: { value: 1, enumerable: true },
  KEYPAD_ACCESS_ONLY: { value: 2, enumerable: true },
  APP_ACCESS_ONLY: { value: 3, enumerable: true },
};

exports.AirbnbInAppMessageTypes = Object.create(Enum, {
  APP_ACCESS_ONLY_SUCCESS: { value: 'App Access Only Success', enumerable: true },
  KEYPAD_ACCESS_ONLY_SUCCESS: { value: 'Keypad Access Only Success', enumerable: true },
  APP_AND_KEYPAD_ACCESS_SUCCESS: { value: 'App and Keypad Success', enumerable: true },
  KEYPAD_ONLY_FINAL_FAILURE: { value: 'Keypad Only Final Failure', enumerable: true },
  APP_AND_KEYPAD_FIRST_FAILURE: { value: 'App and Keypad First Failure', enumerable: true },
  APP_AND_KEYPAD_FINAL_FAILURE: { value: 'App and Keypad Final Failure', enumerable: true },
});

exports.AirbnbKeypadOnlyFirstFailureEmail = 'Keypad Only First Failure Email';

/**
 * https://developer.amazon.com/en-US/docs/alexa/device-apis/alexa-errorresponse.html#error-types
 * @type {any}
 */
exports.AlexaErrorTypes = Object.create(Enum, {
  ALREADY_IN_OPERATION: { value: 'ALREADY_IN_OPERATION', enumerable: true },
  BRIDGE_UNREACHABLE: { value: 'BRIDGE_UNREACHABLE', enumerable: true },
  CLOUD_CONTROL_DISABLED: { value: 'CLOUD_CONTROL_DISABLED', enumerable: true },
  ENDPOINT_BUSY: { value: 'ENDPOINT_BUSY', enumerable: true },
  ENDPOINT_CONTROL_UNAVAILABLE: { value: 'ENDPOINT_CONTROL_UNAVAILABLE', enumerable: true },
  ENDPOINT_LOW_POWER: { value: 'ENDPOINT_LOW_POWER', enumerable: true },
  ENDPOINT_UNREACHABLE: { value: 'ENDPOINT_UNREACHABLE', enumerable: true },
  EXPIRED_AUTHORIZATION_CREDENTIAL: { value: 'EXPIRED_AUTHORIZATION_CREDENTIAL', enumerable: true },
  FIRMWARE_OUT_OF_DATE: { value: 'FIRMWARE_OUT_OF_DATE', enumerable: true },
  HARDWARE_MALFUNCTION: { value: 'HARDWARE_MALFUNCTION', enumerable: true },
  INSUFFICIENT_PERMISSIONS: { value: 'INSUFFICIENT_PERMISSIONS', enumerable: true },
  INTERNAL_ERROR: { value: 'INTERNAL_ERROR', enumerable: true },
  INVALID_AUTHORIZATION_CREDENTIAL: { value: 'INVALID_AUTHORIZATION_CREDENTIAL', enumerable: true },
  INVALID_DIRECTIVE: { value: 'INVALID_DIRECTIVE', enumerable: true },
  INVALID_VALUE: { value: 'INVALID_VALUE', enumerable: true },
  NO_SUCH_ENDPOINT: { value: 'NO_SUCH_ENDPOINT', enumerable: true },
  NOT_CALIBRATED: { value: 'NOT_CALIBRATED', enumerable: true },
  NOT_IN_OPERATION: { value: 'NOT_IN_OPERATION', enumerable: true },
  NOT_SUPPORTED_IN_CURRENT_MODE: { value: 'NOT_SUPPORTED_IN_CURRENT_MODE', enumerable: true },
  NOT_SUPPORTED_WITH_CURRENT_BATTERY_CHARGE_STATE: {
    value: 'NOT_SUPPORTED_WITH_CURRENT_BATTERY_CHARGE_STATE',
    enumerable: true,
  },
  PARTNER_APPLICATION_REDIRECTION: { value: 'PARTNER_APPLICATION_REDIRECTION', enumerable: true },
  POWER_LEVEL_NOT_SUPPORTED: { value: 'POWER_LEVEL_NOT_SUPPORTED', enumerable: true },
  RATE_LIMIT_EXCEEDED: { value: 'RATE_LIMIT_EXCEEDED', enumerable: true },
  TEMPERATURE_VALUE_OUT_OF_RANGE: { value: 'TEMPERATURE_VALUE_OUT_OF_RANGE', enumerable: true },
  TOO_MANY_FAILED_ATTEMPTS: { value: 'TOO_MANY_FAILED_ATTEMPTS', enumerable: true },
});
